import { Autocomplete, AutocompleteChangeDetails, Button, Grid, TextField, Tooltip } from '@mui/material';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { searchContactName } from '../ContactSelector/ContactSelector';
import CustomAccordion from '../CustomAccordion/CustomAccordion';
import TagsFilter from '../TagFilter/TagsFilter';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import SavedSearch, { ComponentType } from '../SavedSearch/SavedSearch';
import Swal from 'sweetalert2';
import SavedSearchApi, { SaveSavedSearchParam } from '../SavedSearch/api/SavedSearchApi';
import SavedSearchModel from '../SavedSearch/models/SavedSearchModel';
import { addSavedSearch, savedSearchSelector } from '../SavedSearch/reducers/SavedSearchReducer';
import { contactInputSelector } from './reducers/ContactInputReducer';
import { ContactSearchSelector, resetContactSearch, setCompanyNames, setContactAccountNames, setContactContactSearchKeyword, setContactSearchResults, setSearchResultTotalRecord, setTriggerContactSearch, setTriggerFocusCompanyName, setCompanyTypes, setCountries, setInterests, setSizeFocuses, setInactiveDate, setEmails } from './reducers/ContactSearchReducer';
import { QueryItem } from '../Tonnage/models/TonnageQueryModel';
import ContactApi, { SearchContactParam } from './api/ContactApi';
import ContactModel from './models/ContactModel';

const ContactSearch: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const contactSearchData = useSelector(ContactSearchSelector);
    const savedSearchData = useSelector(savedSearchSelector);
    const contactInputViewData = useSelector(contactInputSelector);
    const [inactiveDates, setInactiveDates] = useState<string[]>([]);

    useEffect(() => {
        setInactiveDates([
            "15 days",
            "30 days",
            "45 days",
            "60 days",
            "75 days",
            "90 days",
        ])
    }, []);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (contactSearchData.contactSearchKeyword && contactSearchData.contactSearchKeyword.length > 0) {
                searchContactName(contactSearchData.contactSearchKeyword, (contacts) => {
                    if (contacts && contacts.length > 0) {
                        dispatch(setContactAccountNames(contacts));
                    } else {
                        dispatch(setContactAccountNames([]));
                    }
                })
            }
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [contactSearchData.contactSearchKeyword])

    const saveSavedSearch = () => {
        Swal.fire({
            title: 'Provide Saved Search Name',
            input: 'text',
            inputAttributes: {
                autocapitalize: 'off'
            },
            showCancelButton: true,
            confirmButtonText: 'Sumbit',
            showLoaderOnConfirm: true,
            allowOutsideClick: false,
            confirmButtonColor: '#1976d2',
            preConfirm: (name) => {
                if (!name || name.length === 0) {
                    Swal.showValidationMessage("Name ")
                }
            },
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(showLoading(true));

                SavedSearchApi.Save({
                    name: result.value ? result.value : "",
                    component: ComponentType.Contact,
                    queryJson: JSON.stringify(contactSearchData.query)
                } as SaveSavedSearchParam)
                    .then((response: BaseResponse<SavedSearchModel>) => {
                        if (response.success) {
                            dispatch(addSavedSearch(response.data));
                            Utility.Alert.Success("Save Successful");
                        } else {
                            Utility.Alert.Error(response.message);
                        }
                    })
                    .catch((error) => {
                        Utility.Alert.Error(error);
                    })
                    .finally(() => {
                        dispatch(showLoading(false));
                    });
            }
        })
    }

    useEffect(() => {
        if (contactSearchData.triggerSearch === 0) {
            return;
        }

        dispatch(showLoading(true));

        ContactApi.Search({
            query: contactSearchData.query
        } as SearchContactParam)
            .then((response: BaseResponse<ContactModel[]>) => {
                if (response.success) {
                    dispatch(setContactSearchResults(response.data));
                    dispatch(setSearchResultTotalRecord(response.totalRecord));
                } else {
                    Utility.Alert.Error(response.message);
                }
            }).catch((error) => {
                Utility.Alert.Error(error);
            })
            .finally(() => {
                dispatch(showLoading(false));
            });
    }, [contactSearchData.triggerSearch])

    const getTonnageSearchClass = () => {
        let className = "nice-scroll ";
        if (savedSearchData.expanded) {
            className += "tonnage-search-wrapper";
        } else {
            className += "tonnage-search-wrapper-saved-search-hide";
        }

        return className;
    }

    return (
        <>
            <SavedSearch
                component={ComponentType.Contact}
            >
            </SavedSearch>
            <CustomAccordion
                title="Search"
                defaultOpen={true}
                contentClassName={getTonnageSearchClass()}>
                <div className='tonnage-search-body'>
                    <Grid container>
                        <Grid item xs={8}>
                            <Grid container>
                                <Grid xs={8}>
                                    <Tooltip title="Alt + W">
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            className="tonnage-search-save-button"
                                            onClick={() => {
                                                dispatch(setTriggerContactSearch((new Date()).getTime()));
                                            }}
                                        >
                                            RUN SEARCH (w)
                                        </Button>
                                    </Tooltip>
                                </Grid>
                                <Grid xs={4}>
                                    <Tooltip title="Reset (Alt + Q)">
                                        <Button
                                            className="clear-input-button"
                                            color="success"
                                            variant="contained"
                                            onClick={() => {
                                                dispatch(resetContactSearch());
                                                dispatch(setTriggerFocusCompanyName(new Date().getTime()));
                                            }}
                                        >
                                            <i className="fa fa-refresh" aria-hidden="true"></i>
                                        </Button>
                                    </Tooltip>
                                </Grid>
                            </Grid>

                        </Grid>
                        <Grid item xs={4}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => {
                                    saveSavedSearch();
                                }}
                            >
                                Save Search
                            </Button>
                        </Grid>
                    </Grid>
                    <TagsFilter
                        data={_.map(contactSearchData.contactAccountNames, (item) => {
                            return {
                                id: item.id,
                                value: item.companyName,
                                positive: true
                            } as QueryItem
                        })}
                        useFreeText={true}
                        label="Company Name, Sub acct and keywords"
                        onInputChange={(text: string) => {
                            dispatch(setContactContactSearchKeyword(text));
                        }}
                        className="tonnage-search-item"
                        value={contactSearchData.query.companyNames}
                        onValueChange={(value: QueryItem[]) => {
                            dispatch(setCompanyNames(value));
                        }}
                        triggerFocusInput={contactSearchData.triggerFocusCompanyName}
                    ></TagsFilter>
                    <TagsFilter
                        data={_.map(contactInputViewData.staticResources.companyTypes, (item) => {
                            return {
                                id: item,
                                value: item,
                                positive: true
                            } as QueryItem
                        })}
                        label="Company Type"
                        className="tonnage-search-item"
                        value={contactSearchData.query.companyTypes}
                        onValueChange={(value: QueryItem[]) => {
                            dispatch(setCompanyTypes(value));
                        }}
                    ></TagsFilter>
                    <TagsFilter
                        data={_.map(contactInputViewData.staticResources.countries, (item) => {
                            return {
                                id: item.code,
                                value: item.code,
                                positive: true
                            } as QueryItem
                        })}
                        label="Country"
                        className="tonnage-search-item"
                        value={contactSearchData.query.countries}
                        onValueChange={(value: QueryItem[]) => {
                            dispatch(setCountries(value));
                        }}
                    ></TagsFilter>
                    <TagsFilter
                        data={_.map(contactInputViewData.staticResources.interesteds, (item) => {
                            return {
                                id: item,
                                value: item,
                                positive: true
                            } as QueryItem
                        })}
                        label="Interested"
                        className="tonnage-search-item"
                        value={contactSearchData.query.interests}
                        onValueChange={(value: QueryItem[]) => {
                            dispatch(setInterests(value));
                        }}
                    ></TagsFilter>
                    <TagsFilter
                        data={_.map(contactInputViewData.staticResources.sizeFocus, (item) => {
                            return {
                                id: item.key,
                                value: item.key,
                                positive: true
                            } as QueryItem
                        })}
                        label="Company Size"
                        className="tonnage-search-item"
                        value={contactSearchData.query.sizeFocuses}
                        onValueChange={(value: QueryItem[]) => {
                            dispatch(setSizeFocuses(value));
                        }}
                    ></TagsFilter>
                    <TagsFilter
                        data={[]}
                        useFreeText={true}
                        label="Emails"
                        className="tonnage-search-item"
                        value={contactSearchData.query.emails}
                        onValueChange={(value: QueryItem[]) => {
                            dispatch(setEmails(value));
                        }}
                    ></TagsFilter>
                    <Autocomplete
                        className='cargo-input-component'
                        options={inactiveDates}
                        inputValue={contactSearchData.query.inactiveDate}
                        freeSolo={true}
                        renderInput={(params) => <TextField {...params} label="Inactive Date" />}
                        value={contactSearchData.query.inactiveDate}
                        onChange={(event: React.SyntheticEvent, value: string | null, reason: string, details?: AutocompleteChangeDetails<string> | undefined) => {
                            dispatch(setInactiveDate(value ? value : ""));
                        }}
                        onInputChange={(event: React.SyntheticEvent, value: string, reason: string) => {
                            if (value && value.length <= 3) {
                                if (parseInt(value).toString() == value) {
                                    dispatch(setInactiveDate(value ? value : ""))
                                } else {
                                    dispatch(setInactiveDate(contactSearchData.query.inactiveDate))
                                }
                            } else {
                                dispatch(setInactiveDate(contactSearchData.query.inactiveDate))
                            }
                        }}
                    />
                </div>
            </CustomAccordion>
        </>
    )
};

export default ContactSearch;